/**
 * Export PDF "Dossier complet de litige" — synthèse + timeline + pièces jointes embarquées.
 * - Génère la synthèse via jsPDF + autoTable
 * - Fusionne les PDF joints via pdf-lib
 * - Embarque les images (jpg/png) en pages annexes
 * - Liste les autres types non embarquables
 */
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import { api, formatCurrency, formatDate } from "./api";

const THIRD_PARTY_LABELS = {
  client: "Client", supplier: "Fournisseur", subcontractor: "Sous-traitant",
  employee: "Salarié", administration: "Administration", insurer: "Assureur",
  banker: "Banquier", interim_agency: "Agence intérim", other: "Autre",
};
const STATUS_LABELS = {
  to_handle: "À traiter", negotiating: "En négociation",
  litigation: "Au contentieux", waiting: "En attente", closed: "Clos",
};
const SEVERITY_LABELS = {
  low: "Faible", medium: "Moyen", high: "Fort", critical: "Critique",
};
const CATEGORY_LABELS = {
  paiement: "Paiement", qualite: "Qualité", delais: "Délais",
  contractuel: "Contractuel", rh: "RH", fiscal: "Fiscal", autre: "Autre",
};
const OUTCOME_LABELS = {
  amicable: "Accord amiable", abandoned: "Abandon",
  won: "Procès gagné", lost: "Procès perdu",
};

const ORG_NAME = "Bati Assistant";

function _buildSynthesisPdf(dispute) {
  const pdf = new jsPDF({ unit: "mm", format: "a4" });
  const pageW = pdf.internal.pageSize.getWidth();
  const margin = 14;

  // Header
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(16);
  pdf.text("Dossier de litige", margin, 18);
  pdf.setFontSize(9);
  pdf.setFont("helvetica", "normal");
  pdf.setTextColor(120);
  pdf.text(`${ORG_NAME} — généré le ${new Date().toLocaleDateString("fr-FR")} à ${new Date().toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}`, margin, 24);
  pdf.setTextColor(0);

  // Severity badge
  const sevColor = {
    low: [100, 116, 139], medium: [202, 138, 4],
    high: [234, 88, 12], critical: [220, 38, 38],
  }[dispute.severity] || [100, 116, 139];
  pdf.setFillColor(...sevColor);
  pdf.setTextColor(255);
  pdf.setFontSize(9);
  pdf.setFont("helvetica", "bold");
  const sevLabel = `Gravité : ${SEVERITY_LABELS[dispute.severity] || dispute.severity}`;
  const sevW = pdf.getTextWidth(sevLabel) + 6;
  pdf.roundedRect(pageW - margin - sevW, 14, sevW, 7, 1.5, 1.5, "F");
  pdf.text(sevLabel, pageW - margin - sevW + 3, 19);
  pdf.setTextColor(0);

  const tp = THIRD_PARTY_LABELS[dispute.third_party_type] || dispute.third_party_type;
  const tpFull = dispute.third_party_type === "other" && dispute.third_party_other_label
    ? `${tp} — ${dispute.third_party_other_label}` : tp;

  const rows = [
    ["Tiers concerné", `${dispute.third_party_name}${dispute.blacklisted ? "  ⚠ BLACKLISTÉ" : ""}`],
    ["Type", tpFull],
    ["Catégorie", CATEGORY_LABELS[dispute.category] || dispute.category],
    ["Statut", STATUS_LABELS[dispute.status] || dispute.status],
    ["Enjeu financier", dispute.amount_at_stake ? `${formatCurrency(dispute.amount_at_stake)}${dispute.provision_made ? " (provision effectuée)" : ""}` : "—"],
    ["Responsable interne", dispute.internal_owner_name || dispute.internal_owner_id || "—"],
    ["Prochaine action", dispute.next_action || "—"],
    ["Échéance", dispute.next_action_due_date ? formatDate(dispute.next_action_due_date) : "—"],
    ["Créé le", dispute.created_at ? formatDate(dispute.created_at) : "—"],
  ];
  if (dispute.order_id || (Array.isArray(dispute.order_ids) && dispute.order_ids.length > 0)) {
    const ids = new Set();
    if (Array.isArray(dispute.order_ids)) dispute.order_ids.forEach(o => o && ids.add(o));
    if (dispute.order_id) ids.add(dispute.order_id);
    const label = ids.size > 1
      ? `${ids.size} commandes rattachées`
      : (dispute.order_reference || Array.from(ids)[0] || "");
    rows.splice(2, 0, [ids.size > 1 ? "Commandes liées" : "Commande liée", label]);
  }
  if (dispute.status === "closed") {
    rows.push(
      ["Date de clôture", dispute.closed_at ? formatDate(dispute.closed_at) : "—"],
      ["Issue", OUTCOME_LABELS[dispute.closed_outcome] || dispute.closed_outcome || "—"],
      ["Commentaire de clôture", dispute.closing_comment || "—"],
    );
  }

  autoTable(pdf, {
    startY: 30,
    head: [["Synthèse", ""]],
    body: rows,
    theme: "grid",
    headStyles: { fillColor: [30, 41, 59], textColor: 255, fontSize: 10 },
    styles: { fontSize: 9, cellPadding: 2 },
    columnStyles: { 0: { fontStyle: "bold", cellWidth: 55, fillColor: [248, 250, 252] } },
    margin: { left: margin, right: margin },
  });

  // Motif
  let y = pdf.lastAutoTable.finalY + 6;
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(11);
  pdf.text("Motif du litige", margin, y);
  y += 5;
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9.5);
  const reasonLines = pdf.splitTextToSize(dispute.reason || "—", pageW - 2 * margin);
  pdf.text(reasonLines, margin, y);
  y += reasonLines.length * 4.5 + 4;

  // Historique
  const history = dispute.history || [];
  if (history.length > 0) {
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(11);
    if (y > 250) { pdf.addPage(); y = 18; }
    pdf.text(`Historique des échanges (${history.length})`, margin, y);
    const bodyRows = history.map((h) => [
      formatDate(h.date),
      h.origin === "us" ? "Nous" : "Le tiers",
      h.description || "—",
      (h.attachments || []).length > 0 ? (h.attachments || []).map((a) => a.filename).join("\n") : "—",
    ]);
    autoTable(pdf, {
      startY: y + 3,
      head: [["Date", "Origine", "Description", "Pièces jointes"]],
      body: bodyRows,
      theme: "striped",
      headStyles: { fillColor: [99, 102, 241], textColor: 255, fontSize: 9 },
      styles: { fontSize: 8.5, cellPadding: 2, valign: "top" },
      columnStyles: {
        0: { cellWidth: 22 }, 1: { cellWidth: 18 },
        2: { cellWidth: "auto" }, 3: { cellWidth: 50 },
      },
      margin: { left: margin, right: margin },
    });
    y = pdf.lastAutoTable.finalY + 6;
  } else {
    pdf.setFont("helvetica", "italic");
    pdf.setFontSize(9);
    pdf.setTextColor(120);
    pdf.text("Aucun échange enregistré pour ce dossier.", margin, y);
    pdf.setTextColor(0);
    y += 6;
  }

  // Footer pagination
  const total = pdf.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    pdf.setPage(i);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    pdf.setTextColor(140);
    pdf.text(
      `${ORG_NAME} · Dossier confidentiel · Page ${i} / ${total}`,
      pageW / 2,
      pdf.internal.pageSize.getHeight() - 8,
      { align: "center" },
    );
  }
  return pdf.output("arraybuffer");
}

async function _fetchAttachment(fileId) {
  const url = api.getDisputeAttachmentUrl(fileId);
  const headers = {};
  const token = (typeof localStorage !== "undefined") ? localStorage.getItem("token") : null;
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(url, { headers });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return await res.arrayBuffer();
}

function _isPdf(att) {
  const t = (att.content_type || "").toLowerCase();
  if (t.includes("pdf")) return true;
  return (att.filename || "").toLowerCase().endsWith(".pdf");
}
function _isImageJpg(att) {
  const t = (att.content_type || "").toLowerCase();
  if (t.includes("jpeg") || t.includes("jpg")) return true;
  const n = (att.filename || "").toLowerCase();
  return n.endsWith(".jpg") || n.endsWith(".jpeg");
}
function _isImagePng(att) {
  const t = (att.content_type || "").toLowerCase();
  if (t.includes("png")) return true;
  return (att.filename || "").toLowerCase().endsWith(".png");
}

async function _appendSeparator(merged, title, helvBold) {
  const page = merged.addPage([595.28, 841.89]); // A4
  page.drawRectangle({ x: 0, y: 800, width: 595.28, height: 41.89, color: rgb(0.118, 0.161, 0.231) });
  page.drawText("Pièce jointe", { x: 30, y: 818, size: 10, font: helvBold, color: rgb(1, 1, 1) });
  page.drawText(title, { x: 30, y: 760, size: 14, font: helvBold });
}

async function _embedImage(merged, bytes, mime) {
  const img = mime === "png"
    ? await merged.embedPng(bytes)
    : await merged.embedJpg(bytes);
  // Fit A4 with margins
  const pageW = 595.28, pageH = 841.89, margin = 30;
  const maxW = pageW - 2 * margin;
  const maxH = pageH - 100; // leave room for header
  const scale = Math.min(maxW / img.width, maxH / img.height, 1);
  const w = img.width * scale, h = img.height * scale;
  const page = merged.addPage([pageW, pageH]);
  page.drawImage(img, { x: (pageW - w) / 2, y: (pageH - h) / 2 - 30, width: w, height: h });
  return page;
}

export const generateDisputePdf = async (dispute) => {
  if (!dispute) return;
  // 1. Build synthesis PDF
  const baseBytes = _buildSynthesisPdf(dispute);
  const merged = await PDFDocument.load(baseBytes);
  const helvBold = await merged.embedFont(StandardFonts.HelveticaBold);
  const helv = await merged.embedFont(StandardFonts.Helvetica);

  // 2. Walk attachments
  const allAttachments = (dispute.history || [])
    .flatMap((h) => (h.attachments || []).map((a) => ({ date: h.date, ...a })));

  const skipped = [];
  for (const att of allAttachments) {
    try {
      const bytes = await _fetchAttachment(att.file_id);
      if (_isPdf(att)) {
        const src = await PDFDocument.load(bytes, { ignoreEncryption: true });
        // Separator page first
        const page = merged.addPage([595.28, 841.89]);
        page.drawRectangle({ x: 0, y: 800, width: 595.28, height: 41.89, color: rgb(0.118, 0.161, 0.231) });
        page.drawText(`Pièce jointe — ${att.filename}`, { x: 30, y: 818, size: 10, font: helvBold, color: rgb(1, 1, 1) });
        page.drawText(att.date ? `Date : ${att.date}` : "", { x: 30, y: 760, size: 11, font: helv });
        const copied = await merged.copyPages(src, src.getPageIndices());
        copied.forEach((p) => merged.addPage(p));
      } else if (_isImageJpg(att)) {
        await _appendSeparator(merged, att.filename, helvBold);
        // The drawImage already centered; previous _appendSeparator added header page
        // We replace by embedding image directly on a new page:
        // (Simplification : we keep the header page above as cover; image on next.)
        await _embedImage(merged, bytes, "jpg");
      } else if (_isImagePng(att)) {
        await _appendSeparator(merged, att.filename, helvBold);
        await _embedImage(merged, bytes, "png");
      } else {
        skipped.push(att);
      }
    } catch (e) {
      skipped.push(att);
    }
  }

  // 3. Optional skipped attachments listing
  if (skipped.length > 0) {
    const page = merged.addPage([595.28, 841.89]);
    page.drawText("Pièces jointes non embarquables", { x: 30, y: 790, size: 14, font: helvBold });
    page.drawText("(types non supportés en intégration directe — consulter dans Bati Assistant)", { x: 30, y: 770, size: 9, font: helv, color: rgb(0.45, 0.45, 0.45) });
    let yy = 740;
    skipped.forEach((a) => {
      const line = `• ${a.filename}  (${a.content_type || "type inconnu"}, ${a.size ? (a.size / 1024).toFixed(1) + " Ko" : "?"})`;
      page.drawText(line.slice(0, 100), { x: 30, y: yy, size: 10, font: helv });
      yy -= 16;
      if (yy < 40) return;
    });
  }

  const outBytes = await merged.save();
  const blob = new Blob([outBytes], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const safeName = (dispute.third_party_name || "litige").replace(/[^a-zA-Z0-9-_]/g, "_");
  a.download = `Litige_${safeName}_${dispute.id.slice(0, 8)}.pdf`;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    URL.revokeObjectURL(url);
    a.remove();
  }, 500);
};

export default generateDisputePdf;
